// Hand WPI Calculation Utility
// Extracted from HandOverallRatingNarrative.tsx for reuse in Hands tab display

// Helper function to safely handle null/undefined values (currently unused but kept for future use)
// function safeValue(value: number | null | undefined): number {
//   return value !== null && value !== undefined ? value : 0;
// }

// AMA Combined Values math — shared single source of truth.
import { combine, combineAMAValues } from './amaMath';

/**
 * True when a measurement was actually recorded. Anything else — null, undefined,
 * an empty string, a non-numeric value — means "not examined" and must contribute
 * NOTHING. It must never fall through to a numeric comparison, because 0 degrees
 * is a near-maximum deficit in these tables (thumb opposition 0 cm = 45%), so a
 * blank would otherwise be rated as a severe impairment.
 */
export const isMeasured = (v: unknown): v is number => {
  if (v === null || v === undefined) return false;
  // A blank or whitespace-only string is not a measurement — and Number('  ')
  // is 0, so it has to be rejected before the numeric check.
  if (typeof v === 'string' && v.trim() === '') return false;
  return Number.isFinite(Number(v));
};


// Lookup tables for digit to hand conversion
const thumbDigitToHandLookup = (digitImpairment: number): number => {
  const lookupTable = [
    { digit: 0, hand: 0 }, { digit: 1, hand: 1 }, { digit: 2, hand: 2 }, { digit: 3, hand: 3 },
    { digit: 4, hand: 4 }, { digit: 5, hand: 5 }, { digit: 6, hand: 6 }, { digit: 7, hand: 7 },
    { digit: 8, hand: 8 }, { digit: 9, hand: 9 }, { digit: 10, hand: 10 }, { digit: 11, hand: 11 },
    { digit: 12, hand: 12 }, { digit: 13, hand: 13 }, { digit: 14, hand: 14 }, { digit: 15, hand: 15 },
    { digit: 16, hand: 16 }, { digit: 17, hand: 17 }, { digit: 18, hand: 18 }, { digit: 19, hand: 19 },
    { digit: 20, hand: 20 }, { digit: 21, hand: 21 }, { digit: 22, hand: 22 }, { digit: 23, hand: 23 },
    { digit: 24, hand: 24 }, { digit: 25, hand: 25 }, { digit: 26, hand: 26 }, { digit: 27, hand: 27 },
    { digit: 28, hand: 28 }, { digit: 29, hand: 29 }, { digit: 30, hand: 30 }, { digit: 31, hand: 31 },
    { digit: 32, hand: 32 }, { digit: 33, hand: 33 }, { digit: 34, hand: 34 }, { digit: 35, hand: 35 },
    { digit: 36, hand: 36 }, { digit: 37, hand: 37 }, { digit: 38, hand: 38 }, { digit: 39, hand: 39 },
    { digit: 40, hand: 40 }
  ];
  
  const entry = lookupTable.find(item => item.digit === digitImpairment);
  return entry ? entry.hand : 0;
};

const fingerDigitToHandLookup = (digitImpairment: number): number => {
  const lookupTable = [
    { digit: 0, hand: 0 }, { digit: 1, hand: 0 }, { digit: 2, hand: 0 }, { digit: 3, hand: 1 },
    { digit: 4, hand: 1 }, { digit: 5, hand: 1 }, { digit: 6, hand: 1 }, { digit: 7, hand: 1 },
    { digit: 8, hand: 2 }, { digit: 9, hand: 2 }, { digit: 10, hand: 2 }, { digit: 11, hand: 2 },
    { digit: 12, hand: 2 }, { digit: 13, hand: 3 }, { digit: 14, hand: 3 }, { digit: 15, hand: 3 },
    { digit: 16, hand: 3 }, { digit: 17, hand: 3 }, { digit: 18, hand: 4 }, { digit: 19, hand: 4 },
    { digit: 20, hand: 4 }, { digit: 21, hand: 4 }, { digit: 22, hand: 4 }, { digit: 23, hand: 5 },
    { digit: 24, hand: 5 }, { digit: 25, hand: 5 }, { digit: 26, hand: 5 }, { digit: 27, hand: 5 },
    { digit: 28, hand: 6 }, { digit: 29, hand: 6 }, { digit: 30, hand: 6 }, { digit: 31, hand: 6 },
    { digit: 32, hand: 6 }, { digit: 33, hand: 7 }, { digit: 34, hand: 7 }, { digit: 35, hand: 7 },
    { digit: 36, hand: 7 }, { digit: 37, hand: 7 }, { digit: 38, hand: 8 }, { digit: 39, hand: 8 },
    { digit: 40, hand: 8 }, { digit: 41, hand: 8 }, { digit: 42, hand: 8 }, { digit: 43, hand: 9 },
    { digit: 44, hand: 9 }, { digit: 45, hand: 9 }, { digit: 46, hand: 9 }, { digit: 47, hand: 9 },
    { digit: 48, hand: 10 }, { digit: 49, hand: 10 }, { digit: 50, hand: 10 }, { digit: 51, hand: 10 },
    { digit: 52, hand: 10 }, { digit: 53, hand: 11 }, { digit: 54, hand: 11 }, { digit: 55, hand: 11 },
    { digit: 56, hand: 11 }, { digit: 57, hand: 11 }, { digit: 58, hand: 12 }, { digit: 59, hand: 12 },
    { digit: 60, hand: 12 }, { digit: 61, hand: 12 }, { digit: 62, hand: 12 }, { digit: 63, hand: 13 },
    { digit: 64, hand: 13 }, { digit: 65, hand: 13 }, { digit: 66, hand: 13 }, { digit: 67, hand: 13 },
    { digit: 68, hand: 14 }, { digit: 69, hand: 14 }, { digit: 70, hand: 14 }, { digit: 71, hand: 14 },
    { digit: 72, hand: 14 }, { digit: 73, hand: 15 }, { digit: 74, hand: 15 }, { digit: 75, hand: 15 },
    { digit: 76, hand: 15 }, { digit: 77, hand: 15 }, { digit: 78, hand: 16 }, { digit: 79, hand: 16 },
    { digit: 80, hand: 16 }, { digit: 81, hand: 16 }, { digit: 82, hand: 16 }, { digit: 83, hand: 17 },
    { digit: 84, hand: 17 }, { digit: 85, hand: 17 }, { digit: 86, hand: 17 }, { digit: 87, hand: 17 },
    { digit: 88, hand: 18 }, { digit: 89, hand: 18 }, { digit: 90, hand: 18 }, { digit: 91, hand: 18 },
    { digit: 92, hand: 18 }, { digit: 93, hand: 19 }, { digit: 94, hand: 19 }, { digit: 95, hand: 19 },
    { digit: 96, hand: 19 }, { digit: 97, hand: 19 }, { digit: 98, hand: 20 }, { digit: 99, hand: 20 },
    { digit: 100, hand: 20 }
  ];
  
  const entry = lookupTable.find(item => item.digit === digitImpairment);
  return entry ? entry.hand : 0;
};

// Hand to Upper Extremity conversion (Table 16-2)
const handToUpperExtremityLookup = (handImpairment: number): number => {
  const lookupTable = [
    { hand: 0, ue: 0 }, { hand: 1, ue: 1 }, { hand: 2, ue: 2 }, { hand: 3, ue: 3 },
    { hand: 4, ue: 4 }, { hand: 5, ue: 5 }, { hand: 6, ue: 5 }, { hand: 7, ue: 6 },
    { hand: 8, ue: 7 }, { hand: 9, ue: 8 }, { hand: 10, ue: 9 }, { hand: 11, ue: 10 },
    { hand: 12, ue: 11 }, { hand: 13, ue: 12 }, { hand: 14, ue: 13 }, { hand: 15, ue: 14 },
    { hand: 16, ue: 14 }, { hand: 17, ue: 15 }, { hand: 18, ue: 16 }, { hand: 19, ue: 17 },
    { hand: 20, ue: 18 }, { hand: 21, ue: 19 }, { hand: 22, ue: 20 }, { hand: 23, ue: 21 },
    { hand: 24, ue: 22 }, { hand: 25, ue: 22 }, { hand: 26, ue: 23 }, { hand: 27, ue: 24 },
    { hand: 28, ue: 25 }, { hand: 29, ue: 26 }, { hand: 30, ue: 27 }, { hand: 31, ue: 28 },
    { hand: 32, ue: 29 }, { hand: 33, ue: 30 }, { hand: 34, ue: 31 }, { hand: 35, ue: 31 },
    { hand: 36, ue: 32 }, { hand: 37, ue: 33 }, { hand: 38, ue: 34 }, { hand: 39, ue: 35 },
    { hand: 40, ue: 36 }, { hand: 41, ue: 37 }, { hand: 42, ue: 38 }, { hand: 43, ue: 39 },
    { hand: 44, ue: 40 }, { hand: 45, ue: 40 }, { hand: 46, ue: 41 }, { hand: 47, ue: 42 },
    { hand: 48, ue: 43 }, { hand: 49, ue: 44 }, { hand: 50, ue: 45 }, { hand: 51, ue: 46 },
    { hand: 52, ue: 47 }, { hand: 53, ue: 48 }, { hand: 54, ue: 49 }, { hand: 55, ue: 49 },
    { hand: 56, ue: 50 }, { hand: 57, ue: 51 }, { hand: 58, ue: 52 }, { hand: 59, ue: 53 },
    { hand: 60, ue: 54 }, { hand: 61, ue: 55 }, { hand: 62, ue: 56 }, { hand: 63, ue: 57 },
    { hand: 64, ue: 58 }, { hand: 65, ue: 58 }, { hand: 66, ue: 59 }, { hand: 67, ue: 60 },
    { hand: 68, ue: 61 }, { hand: 69, ue: 62 }, { hand: 70, ue: 63 }, { hand: 71, ue: 64 },
    { hand: 72, ue: 65 }, { hand: 73, ue: 66 }, { hand: 74, ue: 67 }, { hand: 75, ue: 67 },
    { hand: 76, ue: 68 }, { hand: 77, ue: 69 }, { hand: 78, ue: 70 }, { hand: 79, ue: 71 },
    { hand: 80, ue: 72 }, { hand: 81, ue: 73 }, { hand: 82, ue: 74 }, { hand: 83, ue: 75 },
    { hand: 84, ue: 76 }, { hand: 85, ue: 76 }, { hand: 86, ue: 77 }, { hand: 87, ue: 78 },
    { hand: 88, ue: 79 }, { hand: 89, ue: 80 }, { hand: 90, ue: 81 }, { hand: 91, ue: 82 },
    { hand: 92, ue: 83 }, { hand: 93, ue: 84 }, { hand: 94, ue: 85 }, { hand: 95, ue: 85 },
    { hand: 96, ue: 86 }, { hand: 97, ue: 87 }, { hand: 98, ue: 88 }, { hand: 99, ue: 89 },
    { hand: 100, ue: 90 }
  ];
  
  const entry = lookupTable.find(item => item.hand === handImpairment);
  if (entry) {
    return entry.ue;
  }
  
  // Fallback: if exact value not found, use linear interpolation or closest value
  if (handImpairment > 100) return 90; // Max UE is 90%
  if (handImpairment < 0) return 0;
  
  // Find closest values for interpolation
  for (let i = 0; i < lookupTable.length - 1; i++) {
    if (handImpairment >= lookupTable[i].hand && handImpairment <= lookupTable[i + 1].hand) {
      // Linear interpolation between the two closest values
      const lower = lookupTable[i];
      const upper = lookupTable[i + 1];
      const ratio = (handImpairment - lower.hand) / (upper.hand - lower.hand);
      return Math.round(lower.ue + ratio * (upper.ue - lower.ue));
    }
  }
  
  return 0; // Fallback
};

// Upper Extremity to Whole Person Impairment conversion (Table 16-3)
const upperExtremityToWPILookup = (ueImpairment: number): number => {
  const lookupTable = [
    { ue: 0, wpi: 0 }, { ue: 1, wpi: 1 }, { ue: 2, wpi: 1 }, { ue: 3, wpi: 2 },
    { ue: 4, wpi: 2 }, { ue: 5, wpi: 3 }, { ue: 6, wpi: 4 }, { ue: 7, wpi: 4 },
    { ue: 8, wpi: 5 }, { ue: 9, wpi: 5 }, { ue: 10, wpi: 6 }, { ue: 11, wpi: 7 },
    { ue: 12, wpi: 7 }, { ue: 13, wpi: 8 }, { ue: 14, wpi: 8 }, { ue: 15, wpi: 9 },
    { ue: 16, wpi: 10 }, { ue: 17, wpi: 10 }, { ue: 18, wpi: 11 }, { ue: 19, wpi: 11 },
    { ue: 20, wpi: 12 }, { ue: 21, wpi: 13 }, { ue: 22, wpi: 13 }, { ue: 23, wpi: 14 },
    { ue: 24, wpi: 14 }, { ue: 25, wpi: 15 }, { ue: 26, wpi: 16 }, { ue: 27, wpi: 16 },
    { ue: 28, wpi: 17 }, { ue: 29, wpi: 17 }, { ue: 30, wpi: 18 }, { ue: 31, wpi: 19 },
    { ue: 32, wpi: 19 }, { ue: 33, wpi: 20 }, { ue: 34, wpi: 20 }, { ue: 35, wpi: 21 },
    { ue: 36, wpi: 22 }, { ue: 37, wpi: 22 }, { ue: 38, wpi: 23 }, { ue: 39, wpi: 23 },
    { ue: 40, wpi: 24 }, { ue: 41, wpi: 25 }, { ue: 42, wpi: 25 }, { ue: 43, wpi: 26 },
    { ue: 44, wpi: 26 }, { ue: 45, wpi: 27 }, { ue: 46, wpi: 28 }, { ue: 47, wpi: 28 },
    { ue: 48, wpi: 29 }, { ue: 49, wpi: 29 }, { ue: 50, wpi: 30 }, { ue: 51, wpi: 31 },
    { ue: 52, wpi: 31 }, { ue: 53, wpi: 32 }, { ue: 54, wpi: 32 }, { ue: 55, wpi: 33 },
    { ue: 56, wpi: 34 }, { ue: 57, wpi: 34 }, { ue: 58, wpi: 35 }, { ue: 59, wpi: 35 },
    { ue: 60, wpi: 36 }, { ue: 61, wpi: 37 }, { ue: 62, wpi: 37 }, { ue: 63, wpi: 38 },
    { ue: 64, wpi: 38 }, { ue: 65, wpi: 39 }, { ue: 66, wpi: 40 }, { ue: 67, wpi: 40 },
    { ue: 68, wpi: 41 }, { ue: 69, wpi: 41 }, { ue: 70, wpi: 42 }, { ue: 71, wpi: 43 },
    { ue: 72, wpi: 43 }, { ue: 73, wpi: 44 }, { ue: 74, wpi: 44 }, { ue: 75, wpi: 45 },
    { ue: 76, wpi: 46 }, { ue: 77, wpi: 46 }, { ue: 78, wpi: 47 }, { ue: 79, wpi: 47 },
    { ue: 80, wpi: 48 }, { ue: 81, wpi: 49 }, { ue: 82, wpi: 49 }, { ue: 83, wpi: 50 },
    { ue: 84, wpi: 50 }, { ue: 85, wpi: 51 }, { ue: 86, wpi: 52 }, { ue: 87, wpi: 52 },
    { ue: 88, wpi: 53 }, { ue: 89, wpi: 53 }, { ue: 90, wpi: 54 }
  ];
  
  const entry = lookupTable.find(item => item.ue === ueImpairment);
  if (entry) {
    return entry.wpi;
  }
  
  // Fallback: if exact value not found, use linear interpolation or closest value
  if (ueImpairment > 90) return 54; // Max WPI is 54%
  if (ueImpairment < 0) return 0;
  
  // Find closest values for interpolation
  for (let i = 0; i < lookupTable.length - 1; i++) {
    if (ueImpairment >= lookupTable[i].ue && ueImpairment <= lookupTable[i + 1].ue) {
      // Linear interpolation between the two closest values
      const lower = lookupTable[i];
      const upper = lookupTable[i + 1];
      const ratio = (ueImpairment - lower.ue) / (upper.ue - lower.ue);
      return Math.round(lower.wpi + ratio * (upper.wpi - lower.wpi));
    }
  }
  
  return 0; // Fallback
};

// Motion inputs mirror the reference parseInt; sensory and amputation retain decimals.
const wholeMotion = (val: number): number => Math.trunc(Number(val));

// Thumb calculation functions - EXACT match to n8n workflow
const calcThumbIPFlexion = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 80, imp: 0 }, { deg: 70, imp: 1 }, { deg: 60, imp: 1 }, { deg: 50, imp: 2 },
    { deg: 40, imp: 3 }, { deg: 30, imp: 4 }, { deg: 20, imp: 4 }, { deg: 10, imp: 6 },
    { deg: 0, imp: 8 }, { deg: -10, imp: 11 }, { deg: -20, imp: 13 }, { deg: -30, imp: 15 }
  ];

  if (val >= 80) return 0;
  if (val <= -30) return 15;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcThumbIPExtension = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: -30, imp: 0 }, { deg: -20, imp: 0 }, { deg: -10, imp: 0 }, { deg: 0, imp: 1 },
    { deg: 10, imp: 2 }, { deg: 20, imp: 3 }, { deg: 30, imp: 5 }, { deg: 40, imp: 7 },
    { deg: 50, imp: 9 }, { deg: 60, imp: 11 }, { deg: 70, imp: 13 }, { deg: 80, imp: 15 }
  ];

  if (val <= -30) return 0;
  if (val >= 80) return 15;

  for (let i = 0; i < anchors.length - 1; i++) {
    const lower = anchors[i];
    const upper = anchors[i + 1];
    if (val >= lower.deg && val <= upper.deg) {
      const ratio = (val - lower.deg) / (upper.deg - lower.deg);
      const impairment = lower.imp + ratio * (upper.imp - lower.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcThumbMCPFlexion = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 60, imp: 0 }, { deg: 50, imp: 1 }, { deg: 40, imp: 2 }, { deg: 30, imp: 3 },
    { deg: 20, imp: 4 }, { deg: 10, imp: 5 }, { deg: 0, imp: 6 }, { deg: -10, imp: 7 },
    { deg: -20, imp: 8 }, { deg: -30, imp: 9 }, { deg: -40, imp: 10 }
  ];

  if (val >= 60) return 0;
  if (val <= -40) return 10;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcThumbMCPExtension = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: -40, imp: 0 }, { deg: -30, imp: 0 }, { deg: -20, imp: 0 }, { deg: -10, imp: 0 },
    { deg: 0, imp: 0 }, { deg: 10, imp: 1 }, { deg: 20, imp: 1 }, { deg: 30, imp: 3 },
    { deg: 40, imp: 5 }, { deg: 50, imp: 8 }, { deg: 60, imp: 10 }
  ];

  if (val <= -40) return 0;
  if (val >= 60) return 10;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val >= upper.deg && val <= lower.deg) {
      const ratio = (val - upper.deg) / (lower.deg - upper.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcThumbCMCRadialAbduction = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 50, imp: 0 }, { deg: 45, imp: 0 }, { deg: 40, imp: 2 }, { deg: 35, imp: 3 },
    { deg: 30, imp: 5 }, { deg: 25, imp: 7 }, { deg: 20, imp: 9 }, { deg: 15, imp: 10 }
  ];

  if (val >= 50) return 0;
  if (val <= 15) return 10;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcThumbCMCAdduction = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 0, imp: 0 }, { deg: 1, imp: 0 }, { deg: 2, imp: 1 }, { deg: 3, imp: 3 },
    { deg: 4, imp: 4 }, { deg: 5, imp: 6 }, { deg: 6, imp: 8 }, { deg: 7, imp: 13 }, { deg: 8, imp: 20 }
  ];

  if (val <= 0) return 0;
  if (val >= 8) return 20;

  for (let i = 0; i < anchors.length - 1; i++) {
    const lower = anchors[i];
    const upper = anchors[i + 1];
    if (val >= lower.deg && val <= upper.deg) {
      const ratio = (val - lower.deg) / (upper.deg - lower.deg);
      const impairment = lower.imp + ratio * (upper.imp - lower.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcThumbCMCOpposition = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 8, imp: 0 }, { deg: 7, imp: 1 }, { deg: 6, imp: 3 }, { deg: 5, imp: 5 },
    { deg: 4, imp: 9 }, { deg: 3, imp: 13 }, { deg: 2, imp: 22 }, { deg: 1, imp: 31 }, { deg: 0, imp: 45 }
  ];

  if (val >= 8) return 0;
  if (val <= 0) return 45;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

// Helper function for two-point discrimination - matches n8n exactly
const twoPointToLossFactor = (mm: number | null | undefined): number => {
  if (!mm) return 0;
  if (mm > 15) return 1;  // Total loss
  if (mm > 6) return 0.5; // Partial loss
  return 0; // No loss
};

// Helper function for triggering severity - matches n8n exactly
const calcTriggerSeverity = (trigger: string | null | undefined): number => {
  if (!trigger) return 0;
  const t = trigger.toLowerCase().trim();
  if (t.includes("none")) return 0;
  if (t.includes("mild")) return 20;
  if (t.includes("moderate")) return 40;
  if (t.includes("severe")) return 60;
  if (t.startsWith("n")) return 0;
  if (t.startsWith("mi")) return 20;
  if (t.startsWith("mod")) return 40;
  if (t.startsWith("s")) return 60;
  return 0;
};

// Helper function for extensor subluxation severity - matches n8n exactly
const calcExtensorSubluxationSeverity = (s: string | null | undefined): number => {
  if (!s) return 0;
  const sub = s.toLowerCase().trim();
  if (sub.includes("none")) return 0;
  if (sub.includes("mild")) return 10;
  if (sub.includes("moderate")) return 20;
  if (sub.includes("severe")) return 30;
  if (sub.startsWith("n")) return 0;
  if (sub.startsWith("mi")) return 10;
  if (sub.startsWith("mod")) return 20;
  if (sub.startsWith("s")) return 30;
  return 0;
};

// Direct digit-to-hand conversion - matches n8n exactly
const convertDigitToHand = (digitPercent: number, digitWorth: number): number => {
  if (digitPercent === null || digitPercent === undefined) return 0;
  return Math.round(digitPercent * (digitWorth / 100));
};

// Finger calculation functions - EXACT match to n8n workflow
const calcDIPFlex = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 70, imp: 0 }, { deg: 60, imp: 5 }, { deg: 50, imp: 10 }, { deg: 40, imp: 15 },
    { deg: 30, imp: 21 }, { deg: 20, imp: 26 }, { deg: 10, imp: 31 }, { deg: 0, imp: 36 },
    { deg: -10, imp: 39 }, { deg: -20, imp: 42 }, { deg: -30, imp: 45 }
  ];

  if (val >= 70) return 0;
  if (val <= -30) return 45;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcDIPExt = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: -30, imp: 0 }, { deg: -20, imp: 0 }, { deg: -10, imp: 0 }, { deg: 0, imp: 0 },
    { deg: 10, imp: 2 }, { deg: 20, imp: 4 }, { deg: 30, imp: 12 }, { deg: 40, imp: 20 },
    { deg: 50, imp: 29 }, { deg: 60, imp: 37 }, { deg: 70, imp: 45 }
  ];

  if (val <= -30) return 0;
  if (val >= 70) return 45;

  for (let i = 0; i < anchors.length - 1; i++) {
    const lower = anchors[i];
    const upper = anchors[i + 1];
    if (val >= lower.deg && val <= upper.deg) {
      const ratio = (val - lower.deg) / (upper.deg - lower.deg);
      const impairment = lower.imp + ratio * (upper.imp - lower.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcPIPFlex = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 100, imp: 0 }, { deg: 90, imp: 6 }, { deg: 80, imp: 12 }, { deg: 70, imp: 18 },
    { deg: 60, imp: 24 }, { deg: 50, imp: 30 }, { deg: 40, imp: 36 }, { deg: 30, imp: 42 },
    { deg: 20, imp: 48 }, { deg: 10, imp: 54 }, { deg: 0, imp: 60 }, { deg: -10, imp: 66 },
    { deg: -20, imp: 73 }, { deg: -30, imp: 80 }
  ];

  if (val >= 100) return 0;
  if (val <= -30) return 80;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcPIPExt = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: -30, imp: 0 }, { deg: -20, imp: 0 }, { deg: -10, imp: 0 }, { deg: 0, imp: 0 },
    { deg: 10, imp: 3 }, { deg: 20, imp: 7 }, { deg: 30, imp: 11 }, { deg: 40, imp: 14 },
    { deg: 50, imp: 25 }, { deg: 60, imp: 36 }, { deg: 70, imp: 47 }, { deg: 80, imp: 58 },
    { deg: 90, imp: 69 }, { deg: 100, imp: 80 }
  ];

  if (val <= -30) return 0;
  if (val >= 100) return 80;

  for (let i = 0; i < anchors.length - 1; i++) {
    const lower = anchors[i];
    const upper = anchors[i + 1];
    if (val >= lower.deg && val <= upper.deg) {
      const ratio = (val - lower.deg) / (upper.deg - lower.deg);
      const impairment = lower.imp + ratio * (upper.imp - lower.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcMCPFlex = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: 90, imp: 0 }, { deg: 80, imp: 6 }, { deg: 70, imp: 11 }, { deg: 60, imp: 17 },
    { deg: 50, imp: 22 }, { deg: 40, imp: 27 }, { deg: 30, imp: 33 }, { deg: 20, imp: 38 },
    { deg: 10, imp: 44 }, { deg: 0, imp: 49 }, { deg: -10, imp: 54 }, { deg: -20, imp: 60 }
  ];

  if (val >= 90) return 0;
  if (val <= -20) return 60;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

const calcMCPExt = (val: number | null | undefined): number => {
  if (!isMeasured(val)) return 0;
  val = wholeMotion(val);
  
  const anchors = [
    { deg: -20, imp: 0 }, { deg: -10, imp: 3 }, { deg: 0, imp: 5 }, { deg: 10, imp: 7 },
    { deg: 20, imp: 10 }, { deg: 30, imp: 12 }, { deg: 40, imp: 27 }, { deg: 50, imp: 41 },
    { deg: 60, imp: 56 }, { deg: 70, imp: 71 }, { deg: 80, imp: 85 }, { deg: 90, imp: 100 }
  ];

  if (val <= -20) return 0;
  if (val >= 90) return 100;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val >= upper.deg && val <= lower.deg) {
      const ratio = (val - upper.deg) / (lower.deg - upper.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

/**
 * Per-motion degree->impairment functions for the hand, exposed so the reference
 * parity test can pin them cell-by-cell against the QME - Tool calculator.
 * Keys match that project's HAND_IMPAIRMENT export.
 */
export const HAND_IMPAIRMENT: Record<string, (deg: number | null | undefined) => number> = {
  thumbIpFlex: calcThumbIPFlexion,
  thumbIpExt: calcThumbIPExtension,
  thumbMcpFlex: calcThumbMCPFlexion,
  thumbMcpExt: calcThumbMCPExtension,
  cmcRadialAbduction: calcThumbCMCRadialAbduction,
  cmcAdduction: calcThumbCMCAdduction,
  cmcOpposition: calcThumbCMCOpposition,
  dipFlex: calcDIPFlex,
  dipExt: calcDIPExt,
  pipFlex: calcPIPFlex,
  pipExt: calcPIPExt,
  mcpFlex: calcMCPFlex,
  mcpExt: calcMCPExt,
};

// Main calculation function for hand WPI display
export const calculateHandWPI = (data: any, side: 'right' | 'left'): number => {
  if (!data) {
    return 0;
  }
  
  // Calculate individual finger impairments - EXACT match to n8n workflow
  const calculateThumbImpairment = (): number => {
    const thumbs = data.thumbs;
    if (!thumbs) return 0;
    
    // Motion impairment calculations - ALL thumb motions like n8n
    const ipFlexResult = calcThumbIPFlexion(thumbs[`${side}_ip_flexion`]);
    const ipExtResult = calcThumbIPExtension(thumbs[`${side}_ip_extension`]);
    const mcpFlexResult = calcThumbMCPFlexion(thumbs[`${side}_mcp_flexion`]);
    const mcpExtResult = calcThumbMCPExtension(thumbs[`${side}_mcp_extension`]);
    const cmcRadAbdResult = calcThumbCMCRadialAbduction(thumbs[`${side}_cmc_radial_abduction`]);
    const cmcAddResult = calcThumbCMCAdduction(thumbs[`${side}_cmc_adduction`]);
    const cmcOppResult = calcThumbCMCOpposition(thumbs[`${side}_cmc_opposition`]);
    
    // Sum all motion impairments (not combined) - matches n8n exactly
    const motion = ipFlexResult + ipExtResult + mcpFlexResult + mcpExtResult + cmcRadAbdResult + cmcAddResult + cmcOppResult;
    
    // Sensory impairment calculations - matches n8n exactly
    const radialSensLevel = thumbs[`${side}_radial_two_point`] || 0;
    const ulnarSensLevel = thumbs[`${side}_ulnar_two_point`] || 0;
    const radialSensPercent = thumbs[`${side}_radial_two_point_percent`] || 0;
    const ulnarSensPercent = thumbs[`${side}_ulnar_two_point_percent`] || 0;
    
    const thumbRadialLoss = twoPointToLossFactor(radialSensLevel);
    const thumbUlnarLoss = twoPointToLossFactor(ulnarSensLevel);
    
    const radialSensory = Math.round(20 * (radialSensPercent / 100) * thumbRadialLoss);
    const ulnarSensory = Math.round(30 * (ulnarSensPercent / 100) * thumbUlnarLoss);
    const sensory = radialSensory + ulnarSensory;
    
    // Triggering severity
    const triggeringSeverity = calcTriggerSeverity(thumbs[`${side}_triggering`]);
    
    // Amputation percentage
    const amputationPercent = thumbs[`${side}_amputation_percent`] || 0;
    
    // Combine all impairments via the AMA Combined Values Chart
    // (largest-first, order-independent)
    const combinedDigit = combineAMAValues([motion, sensory, amputationPercent, triggeringSeverity]);

    // Convert digit to hand using direct multiplication (40% digit worth) - matches n8n exactly
    const digitToHand = convertDigitToHand(combinedDigit, 40);
    
    return digitToHand;
  };
  
  const calculateFingerImpairment = (fingerType: string): number => {
    const fingers = data[fingerType];
    if (!fingers) return 0;
    
    // Motion impairment calculations - EXACT match to n8n workflow
    const dipFlex = calcDIPFlex(fingers[`${side}_dip_flexion`]);
    const dipExt = calcDIPExt(fingers[`${side}_dip_extension`]);
    const pipFlex = calcPIPFlex(fingers[`${side}_pip_flexion`]);
    const pipExt = calcPIPExt(fingers[`${side}_pip_extension`]);
    const mcpFlex = calcMCPFlex(fingers[`${side}_mcp_flexion`]);
    const mcpExt = calcMCPExt(fingers[`${side}_mcp_extension`]);
    
    // Add flexion and extension for each joint (not combined) - matches n8n exactly
    const dipSum = dipFlex + dipExt;
    const pipSum = pipFlex + pipExt;
    const mcpSum = mcpFlex + mcpExt;
    
    // Combine joint impairments via the AMA Combined Values Chart
    // (largest-first, order-independent)
    const totalMotion = combineAMAValues([dipSum, pipSum, mcpSum]);
    
    // Sensory impairment calculations - matches n8n exactly
    const radialSensLevel = fingers[`${side}_radial_two_point`] || 0;
    const ulnarSensLevel = fingers[`${side}_ulnar_two_point`] || 0;
    const radialSensPercent = fingers[`${side}_radial_two_point_percent`] || 0;
    const ulnarSensPercent = fingers[`${side}_ulnar_two_point_percent`] || 0;
    
    const radLoss = twoPointToLossFactor(radialSensLevel);
    const ulnLoss = twoPointToLossFactor(ulnarSensLevel);
    
    // Determine sensory percentages based on finger type - matches n8n exactly
    let radialMax = 30; // Default for index, middle, ring
    let ulnarMax = 20;  // Default for index, middle, ring
    
    if (fingerType === 'small_fingers' || fingerType === 'little_fingers') {
      radialMax = 20;
      ulnarMax = 30;
    }
    
    const radialSensory = Math.round(radialMax * (radialSensPercent / 100) * radLoss);
    const ulnarSensory = Math.round(ulnarMax * (ulnarSensPercent / 100) * ulnLoss);
    const sensory = radialSensory + ulnarSensory;
    
    // Other impairments - matches n8n exactly
    const triggeringSeverity = calcTriggerSeverity(fingers[`${side}_triggering`]);
    const amputationPercent = fingers[`${side}_amputation_percent`] || 0;
    
    // Extensor subluxation over the MP joint (Table 16-30) — a DIGIT impairment
    // that applies to every finger. The little finger used to be excluded here,
    // so a subluxation the Hands tab had collected was silently worth nothing.
    const extensorSubluxationSeverity = calcExtensorSubluxationSeverity(
      fingers[`${side}_extensor_subluxation_mcp`],
    );
    
    // Combine all impairments via the AMA Combined Values Chart
    // (largest-first, order-independent)
    const combinedDigit = combineAMAValues([totalMotion, sensory, amputationPercent, triggeringSeverity, extensorSubluxationSeverity]);
    
    // Determine digit worth based on finger type - matches n8n exactly
    let digitWorth = 20; // Default for index and middle
    if (fingerType === 'ring_fingers' || fingerType === 'small_fingers' || fingerType === 'little_fingers') {
      digitWorth = 10;
    }
    
    // Convert digit to hand using direct multiplication - matches n8n exactly
    const digitToHand = convertDigitToHand(combinedDigit, digitWorth);
    
    return digitToHand;
  };
  
  // Calculate overall hand impairment - matches n8n exactly (addition, not combination)
  const thumbImpairment = calculateThumbImpairment();
  const indexImpairment = calculateFingerImpairment('index_fingers');
  const middleImpairment = calculateFingerImpairment('middle_fingers');
  const ringImpairment = calculateFingerImpairment('ring_fingers');
  // Handle both 'little_fingers' and 'small_fingers' field names from API
  const littleImpairment = calculateFingerImpairment('little_fingers') || calculateFingerImpairment('small_fingers');
  
  // Add all finger impairments together (not combined) - matches n8n exactly
  const overallHandImpairment = thumbImpairment + indexImpairment + middleImpairment + ringImpairment + littleImpairment;
  
  // Convert hand impairment to WPI using n8n method (direct multiplication)
  const upperExtremityImpairment = Math.round(overallHandImpairment * 0.9);
  const wholePersonImpairment = Math.round(upperExtremityImpairment * 0.6);
  
  return wholePersonImpairment;
};
